import React, {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Dialog, DialogContent, DialogFooter, Button} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import ProductPreviewPanel from '../ChangeProduct/ProductPreviewPanel';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {
  AlternativeProductData,
  useChangeProductContext,
} from '../Context/ChangeProduct/ChangeProductContext';
import {CurrencyAdapter, Lang, Localisation} from '@taopix/taopix-core';
import useSettings from '../../Hooks/useSettings';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useTaopix} from '../Context/TaopixContext';

export type UpgradeProductDialogProps = {
  product: AlternativeProductData;
  open: boolean;
  onChangeProduct: (ref: number) => void;
};

const UpgradeProductDialog = ({
  open,
  product,
  onChangeProduct,
  ...props
}: UpgradeProductDialogProps) => {
  const taopix = useTaopix();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const settings = useSettings();
  const {t} = useTranslation();

  const changeProduct = useChangeProductContext();

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const upgradeStepActive = userFlow.isStepActive(UserFlowType.Upgrade, UserFlowStep.Upgrade);

  useEffect(() => {
    if (upgradeStepActive && !changeProduct.state.canUpgradeProduct) {
      userFlow.setNextStep();
    }
  }, [upgradeStepActive]);

  if (!taopix.state.loaded) return <></>;

  return (
    <Dialog
      open={open && Object.keys(changeProduct.state.upgradeProductData).length > 0}
      focusFirstElement
      trapFocus={true}
      id={'upgrade-project'}
      height={400}
      fixedHeight={false}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      title={t('*:str_MessageUpgradeProject')}
    >
      <DialogContent>
        <ProductPreviewPanel
          //prettier-ignore
          selectedProduct={{
              ...product,
              moreInformationURL: Localisation.getLocaleString(Lang.getCode(),product?.moreInformationURL),
              name: Localisation.getLocaleString(Lang.getCode(), product?.name),
              description: Localisation.getLocaleString(Lang.getCode(), product?.description),
            }}
          onChangeProduct={() => {}}
          showPreviewPanelAsDialog={false}
          isChangeProduct={false}
          currency={currency}
        />
      </DialogContent>
      <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
        <Button
          label={t('*:str_ButtonDontUpgrade')}
          buttonStyle={'secondary'}
          onClick={() => {
            userFlow.setNextStep();
          }}
        />
        <Button
          label={t('*:str_ButtonUpgradeNow')}
          buttonStyle={'primary'}
          onClick={() => {
            userFlow.setCurrentStep(`${UserFlowType.Upgrade}.${UserFlowStep.None}`);
            onChangeProduct(product.ref);
          }}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default UpgradeProductDialog;
